import React, { useState, useEffect, useContext, useCallback, useRef, useMemo } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import "./viewpro.css";
import axios from "axios";
import { API_BASE_URL } from "../api_config";
import blur from "../../assets/blur.jpeg";
import UserContext from "../Authcontext";
import { useSearchContext } from "../SearchContext";
import RemainingSearchResults from "../Search/RemainingSearchResults";
import "../Search/remaining-search-results.css";

function ViewProfile() {
  const { user, token } = useContext(UserContext);
  const { setViewedProfile } = useSearchContext();
  const [getProf, setGetProf] = useState({});
  const location = useLocation();
  const navigate = useNavigate();
  const profId = location.state.userId;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState("");

  // New state variables for sensitive data protection
  const [showSensitiveDataModal, setShowSensitiveDataModal] = useState(false);
  const [remainingViews, setRemainingViews] = useState(0);
  const [viewsUsed, setViewsUsed] = useState(0);
  const [allDataRevealed, setAllDataRevealed] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [previouslyViewed, setPreviouslyViewed] = useState(false);
  
  // Add loading states to prevent race conditions and multiple clicks
  const [isLoadingUserData, setIsLoadingUserData] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  const user_id = user.user_id;

  // Membership limits - using useMemo to prevent recreation on every render
  const membershipLimits = useMemo(() => ({
    Silver: 10,
    Gold: 20,
    Diamond: 35,  
    Normal: 0
  }), []);

  // State to track if the user can view this profile
  const [canViewProfile, setCanViewProfile] = useState(false);

  // Set the viewed profile in search context when component mounts - only once
  useEffect(() => {
    if (profId) {
      setViewedProfile(profId);
      // Scroll to top when a new profile is loaded
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [profId, setViewedProfile]);

  // Fetch user membership and view count data
  const fetchUserData = useCallback(async () => {
    if (!user_id || !token) return;
    
    console.log("Fetching user membership data for user ID:", user_id);
    setIsLoadingUserData(true);
    
    try {
      const response = await axios.get(`${API_BASE_URL}/api/user-membership/${user_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log("User membership data received:", response.data);
      
      const { viewverfycount, pay_membership } = response.data;
      const limit = membershipLimits[pay_membership] || 0;
      const remaining = Math.max(0, limit - viewverfycount);
      
      console.log(`Membership: ${pay_membership}, Used: ${viewverfycount}, Limit: ${limit}, Remaining: ${remaining}`);
      
      setRemainingViews(remaining);
      setViewsUsed(viewverfycount);
      setIsLoadingUserData(false);
      
      return { remaining, used: viewverfycount };
    } catch (error) {
      console.error("Error fetching user membership data:", error);
      setIsLoadingUserData(false);
      return null;
    }
  }, [user_id, token, membershipLimits]);

  // Function to check if profile was previously viewed using the database
  // Using useRef to track if we've already checked this profile
  const checkedRef = useRef(false);
  
  const checkPreviouslyViewed = useCallback(() => {
    return new Promise((resolve) => {
      // If we've already checked this profile in this session, don't check again
      if (checkedRef.current) {
        console.log(`[DEBUG] Skipping redundant check for profile ${profId}`);
        resolve(previouslyViewed);
        return;
      }
      
      console.log(`[DEBUG] Checking if profile ${profId} was previously viewed by user ${user?.user_id}`);
      
      // Call the API to check if this profile was viewed
      axios.get(`${API_BASE_URL}/api/is-profile-viewed/${profId}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      .then(response => {
        const wasViewed = response.data.isViewed;
        console.log(`[DEBUG] Profile ${profId} previously viewed (from database): ${wasViewed}`);
        
        // Set state based on previous viewing
        setPreviouslyViewed(wasViewed);
        
        // If previously viewed, automatically reveal the data
        if (wasViewed) {
          console.log(`[DEBUG] Automatically revealing data for previously viewed profile ${profId}`);
          setAllDataRevealed(true);
        } else {
          console.log(`[DEBUG] Profile ${profId} not previously viewed, data remains hidden`);
        }
        
        // Mark as checked so we don't check again in this session
        checkedRef.current = true;
        
        resolve(wasViewed);
      })
      .catch(error => {
        console.error("[DEBUG] Error checking previously viewed status:", error);
        // Fallback to not viewed if there's an error
        setPreviouslyViewed(false);
        resolve(false);
      });
    });
  }, [profId, user?.user_id, token, API_BASE_URL, previouslyViewed]);

  // Reset states when profile changes
  useEffect(() => {
    // Reset our check status when profile changes
    checkedRef.current = false;
    setAllDataRevealed(false);
    setPreviouslyViewed(false);
    setShowSensitiveDataModal(false);
    setShowUpgradeModal(false);
    setIsLoadingUserData(true);
  }, [profId]);

  // Main useEffect for fetching profile data
  useEffect(() => {
    if (!profId || !user) return;

    console.log("received user", user);
    console.log("profId: " + profId);

    const initializeProfile = async () => {
      try {
        console.log(`[DEBUG] Initializing profile ${profId} for user ${user.user_id}`);
        
        // Fetch profile data
        const profileResponse = await axios.get(`${API_BASE_URL}/api/get-prof/${profId}`, { 
          withCredentials: true 
        });
        
        setGetProf({ ...profileResponse.data[0] });
        console.log(`[DEBUG] Profile data fetched successfully`);

        // Scroll to top when profile data is loaded
        window.scrollTo({ top: 0, behavior: 'smooth' });

        // Check view limit
        const viewedCount = user.viewedProfilesCount || 0;
        const limit = membershipLimits[user.pay_membership] || 0;
        console.log(`[DEBUG] User view count: ${viewedCount}/${limit} (${user.pay_membership} membership)`);

        if (viewedCount < limit || user.pay_membership === "Diamond") {
          setCanViewProfile(true);
        } else {
          setCanViewProfile(false);
        }
        
        // Check if profile was previously viewed first - AWAIT THE PROMISE
        console.log(`[DEBUG] Checking if profile was previously viewed...`);
        const wasViewed = await checkPreviouslyViewed();
        console.log(`[DEBUG] Profile previously viewed check result: ${wasViewed}`);
        
        // Then fetch user membership data
        const userData = await fetchUserData();
        console.log(`[DEBUG] User membership data fetched:`, userData);
        
        // If profile was previously viewed, reveal data regardless of count
        if (wasViewed) {
          console.log(`[DEBUG] Setting all data revealed because profile was previously viewed`);
          setAllDataRevealed(true);
        }
        
      } catch (err) {
        console.log(err);
        setIsLoadingUserData(false);
      }
    };

    initializeProfile();
  }, [profId, user?.user_id, user?.pay_membership, user?.viewedProfilesCount, membershipLimits, fetchUserData, checkPreviouslyViewed]);

  // Handler to show image modal
  const toggleImageModal = (imgSrc) => {
    setSelectedImage(imgSrc);
    setIsModalOpen(true);
  };

  // Handle click on sensitive data
  const handleSensitiveDataClick = () => {
    if (!user) {
      navigate('/login');
      return;
    }
    
    // If data is loading, prevent action
    if (isLoadingUserData) {
      console.log("Still loading user data, please wait...");
      return;
    }
    
    // If previously viewed, show data without confirmation or count deduction
    if (previouslyViewed) {
      console.log("Profile previously viewed - showing data without confirmation");
      setAllDataRevealed(true);
      return;
    }
    
    // Check if user has reached their view limit
    const hasReachedLimit = remainingViews <= 0;
    
    // Only premium users with remaining views can view sensitive data
    if (user.pay_membership === 'Normal' || hasReachedLimit) {
      setShowUpgradeModal(true);
      return;
    }
    
    // Show confirmation modal for premium users with remaining views
    setShowSensitiveDataModal(true);
  };

  // Handler to request viewing sensitive data
  const handleRequestSensitiveData = () => {
    // Add loading state to prevent multiple clicks
    if (isLoading) return;
    
    // Don't show confirmation for own profile
    if (user_id === getProf.user_id) {
      // Directly reveal the data for own profile
      setAllDataRevealed(true);
      return;
    }

    // Show modal immediately for better UX
    setShowSensitiveDataModal(true);
  };

  // Handler to confirm viewing sensitive data
  const handleConfirmViewSensitiveData = async () => {
    // Prevent multiple clicks
    if (isLoading) return;
    setIsLoading(true);
    
    console.log("Confirming sensitive data view...");
    
    // Check if user has remaining views
    if (remainingViews <= 0 && user.pay_membership !== "Diamond") {
      console.log("No remaining views available. Cannot view sensitive data.");
      // Close modal without revealing data
      setShowSensitiveDataModal(false);
      setIsLoading(false);
      return;
    }
    
    // Update revealed data state immediately for better UX
    setAllDataRevealed(true);
    console.log("All sensitive data revealed in UI");

    // Close modal immediately for better UX
    setShowSensitiveDataModal(false);

    // Only skip count if viewing own profile or previously viewed
    if (user_id !== getProf.user_id && !previouslyViewed) {
      console.log(`Updating view count for user ${user_id} viewing profile ${getProf.user_id}`);
      console.log(`Before update: Used views = ${viewsUsed}, Remaining = ${remainingViews}`);
      
      try {
        // Run these API calls in parallel for better performance
        const [countResponse, recordResponse] = await Promise.all([
          // Increment view count on backend
          axios.post(`${API_BASE_URL}/api/use-sensitive-data-view`, {
            userId: user_id,
          }, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          
          // Save this profile ID to database as viewed
          axios.post(`${API_BASE_URL}/api/record-viewed-profile`, {
            profileId: parseInt(profId)
          }, {
            headers: { Authorization: `Bearer ${token}` }
          })
        ]);
        
        console.log("View data response:", countResponse.data);
        console.log(`[DEBUG] Successfully recorded profile ${profId} as viewed in database:`, recordResponse.data);
        
        // Mark as previously viewed for this session
        setPreviouslyViewed(true);
        checkedRef.current = true; // Mark as checked to prevent redundant API calls
        
        // Check if limit reached and membership was downgraded
        if (countResponse.data.limitReached) {
          console.log("View limit reached - Membership downgraded to Normal");
          // Show alert to user about membership downgrade
          alert("You have reached your sensitive data view limit. Your membership has been downgraded to Normal. Please upgrade your plan to view more profiles.");
          // Update with reset values from server
          setRemainingViews(0);
          setViewsUsed(0);
        } else {
          // Normal update flow
          // Update remaining views with the response from server
          if (countResponse.data.remainingViews !== undefined) {
            const newRemaining = countResponse.data.remainingViews;
            console.log(`Server returned remaining views: ${newRemaining}`);
            setRemainingViews(newRemaining);
          } else {
            // Fallback to calculated value if server doesn't return it
            const newRemaining = Math.max(0, remainingViews - 1);
            console.log(`Calculated remaining views locally: ${newRemaining}`);
            setRemainingViews(newRemaining);
          }
          
          // Update used count
          const newUsedCount = countResponse.data.viewverfycount || (viewsUsed + 1);
          console.log(`New used count: ${newUsedCount}`);
          setViewsUsed(newUsedCount);
        }
      } catch (error) {
        console.error("[DEBUG] Error processing view request:", error);
        // Don't show error to user for better UX
      }
    } else {
      if (previouslyViewed) {
        console.log("Profile previously viewed - no view count updated");
      } else {
        console.log("Viewing own profile - no view count updated");
      }
    }
    
    // Always reset loading state when done
    setIsLoading(false);
  };

  // Format DOB properly
  function formatDOB(dobString) {
    if (!dobString) return "Not specified";
    const dobDate = new Date(dobString);
    const year = dobDate.getFullYear();
    const month = String(dobDate.getMonth() + 1).padStart(2, "0");
    const day = String(dobDate.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  function numFor(number) {
    const numFormat = Intl.NumberFormat("en-IN");
    return numFormat.format(number);
  }

  return (
    <section style={{padding: "20px 0"}}>
      {/* Loading State */}
      {isLoadingUserData && (
        <div className="profile-loading">
          <div className="loading-spinner"></div>
          <p>Loading profile...</p>
        </div>
      )}

      {/* Image Modal */}
      {isModalOpen && (
        <div className="image-modal" onClick={() => setIsModalOpen(false)}>
          <button className="close-modal" onClick={() => setIsModalOpen(false)}>×</button>
          <img src={selectedImage} alt="Profile" className="modal-image" />
        </div>
      )}

      {/* Sensitive Data Confirmation Modal */}
      {showSensitiveDataModal && (
        <div className="sensitive-data-modal">
          <div className="sensitive-data-content">
            <h3>View Sensitive Information</h3>
            
            {/* Display based on membership type */}
            {user.pay_membership === "Diamond" ? (
              <p>
                You have <span className="view-count">{remainingViews}</span> sensitive data views remaining in your Diamond membership.
              </p>
            ) : (
              <p>
                You have <span className="view-count">{remainingViews}</span> sensitive data views remaining in your {user.pay_membership} membership.
              </p>
            )}
            
            <p>Would you like to use one view to see this information?</p>
            
            <div className="modal-buttons">
              <button className="cancel-btn" onClick={() => setShowSensitiveDataModal(false)}>
                Cancel
              </button>
              {remainingViews > 0 || user.pay_membership === "Diamond" ? (
                <button className="confirm-btn" onClick={handleConfirmViewSensitiveData}>
                  View Data
                </button>
              ) : (
                <Link to="/payment/paymentoption" className="upgrade-link">
                  Upgrade Now
                </Link>
              )}
            </div>
            
            {/* Only show upgrade prompt if views are exhausted and not Diamond */}
            {remainingViews === 0 && user.pay_membership !== "Diamond" && (
              <div className="upgrade-prompt">
                <p>You've used all your views. Upgrade your membership for more!</p>
                <Link to="/payment/paymentoption" className="upgrade-link">
                  Upgrade Now
                </Link>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Only show profile content when data is loaded */}
      {!isLoadingUserData && Object.keys(getProf).length > 0 && (
        <div className="view-prof-container">
          {/* Profile Header */}
          <div className="profile-header">
            <div className="profile-image-container">
              <img
                src={`${API_BASE_URL}/uploads/` + getProf.filename}
                alt={`${getProf.first_name}`}
                className="get-prof-img"
                onClick={() =>
                  toggleImageModal(`${API_BASE_URL}/uploads/` + getProf.filename)
                }
              />
            </div>
            
            <div className="profile-info">
              <h1 className="profile-name">
                {getProf.first_name} {getProf.last_name}
              </h1>
              
              <div className="profile-highlight">
                <div className="highlight-item">
                  <span className="highlight-icon">📅</span>
                  <span className="highlight-text">{getProf.age} years</span>
                </div>
                
                <div className="highlight-item">
                  <span className="highlight-icon">🏠</span>
                  <span className="highlight-text">{getProf.city_name}, {getProf.state_name}</span>
                </div>
                
                <div className="highlight-item">
                  <span className="highlight-icon">💼</span>
                  <span className="highlight-text">{getProf.occupation}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Profile Content */}
          <div className="profile-content">
            {/* Personal Information */}
            <div className="profile-section">
              <h2 className="section-title">Personal Information</h2>
              <div className="info-grid">
                <div className="info-item">
                  <div className="info-label">Date of Birth</div>
                  <div className="info-value">{formatDOB(getProf.dob)}</div>
                </div>
                
                <div className="info-item">
                  <div className="info-label">Marital Status</div>
                  <div className="info-value">{getProf.marital_status}</div>
                </div>
                
                <div className="info-item">
                  <div className="info-label">Mother Tongue</div>
                  <div className="info-value">{getProf.mother_tongue}</div>
                </div>
                
                <div className="info-item">
                  <div className="info-label">Height</div>
                  <div className="info-value">{getProf.height} cm</div>
                </div>
                
                <div className="info-item">
                  <div className="info-label">Weight</div>
                  <div className="info-value">{getProf.weight} kg</div>
                </div>
                
                <div className="info-item">
                  <div className="info-label">Food Habits</div>
                  <div className="info-value">{getProf.food_habits}</div>
                </div>
                
                <div className="info-item">
                  <div className="info-label">Home Address</div>
                  {(user.user_id === getProf.user_id || allDataRevealed) ? (
                    <div className="address-container">
                      <div className="info-value">{getProf.address || "Not specified"}</div>
                      {getProf.address1 && <div className="info-value address-line">{getProf.address1}</div>}
                    </div>
                  ) : (user.pay_membership === "Gold" || user.pay_membership === "Silver" || user.pay_membership === "Diamond") ? (
                    // For paid users who haven't revealed this data yet
                    <div 
                      className="blurred-info clickable" 
                      onClick={handleRequestSensitiveData}
                    >
                      <img
                        src={blur}
                        alt="blurred"
                        style={{ width: "100%", height: "25px", opacity: 0.5 }}
                      />
                      <div className="view-text">Click to view</div>
                    </div>
                  ) : (
                    // For non-paid users
                    <div className="blurred-info">
                      <img
                        src={blur}
                        alt="upgrade"
                        style={{ width: "100%", height: "25px", opacity: 0.5 }}
                      />
                      <div onClick={() => {
                    
                    navigate('/payment/paymentoption');
                  }} className="upgrade-text">Upgrade to view</div>
                    </div>
                  )}
                </div>
                
                <div className="info-item">
                  <div className="info-label">Email</div>
                  {(user.user_id === getProf.user_id || allDataRevealed) ? (
                    <div className="info-value">{getProf.email || "Not specified"}</div>
                  ) : (user.pay_membership === "Gold" || user.pay_membership === "Silver" || user.pay_membership === "Diamond") ? (
                    // For paid users who haven't revealed this data yet
                    <div 
                      className="blurred-info clickable" 
                      onClick={handleRequestSensitiveData}
                    >
                      <img
                        src={blur}
                        alt="blurred"
                        style={{ width: "100%", height: "25px", opacity: 0.5 }}
                      />
                      <div className="view-text">Click to view</div>
                    </div>
                  ) : (
                    // For non-paid users
                    <div className="blurred-info">
                      <img
                        src={blur}
                        alt="upgrade"
                        style={{ width: "100%", height: "25px", opacity: 0.5 }}
                      />
                      <div onClick={() => {
                    
                    navigate('/payment/paymentoption');
                  }} className="upgrade-text">Upgrade to view</div>
                    </div>
                  )}
                </div>
                
                <div className="info-item">
                  <div className="info-label">Mobile Number</div>
                  {(user.user_id === getProf.user_id || allDataRevealed) ? (
                    <div className="info-value">{getProf.mobile || "Not specified"}</div>
                  ) : (user.pay_membership === "Gold" || user.pay_membership === "Silver" || user.pay_membership === "Diamond") ? (
                    // For paid users who haven't revealed this data yet
                    <div 
                      className="blurred-info clickable" 
                      onClick={handleRequestSensitiveData}
                    >
                      <img
                        src={blur}
                        alt="blurred"
                        style={{ width: "100%", height: "25px", opacity: 0.5 }}
                      />
                      <div className="view-text">Click to view</div>
                    </div>
                  ) : (
                    // For non-paid users
                    <div className="blurred-info">
                      <img
                        src={blur}
                        alt="upgrade"
                        style={{ width: "100%", height: "25px", opacity: 0.5 }}
                      />
                      <div onClick={() => {
                    
                    navigate('/payment/paymentoption');
                  }} className="upgrade-text">Upgrade to view</div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Professional Information */}
            <div className="profile-section">
              <h2 className="section-title">Professional Information</h2>
              <div className="info-grid">
                <div className="info-item">
                  <div className="info-label">Education</div>
                  <div className="info-value">{getProf.education}</div>
                </div>
                
                <div className="info-item">
                  <div className="info-label">Occupation</div>
                  <div className="info-value">{getProf.occupation}</div>
                </div>
                
                <div className="info-item">
                  <div className="info-label">Employment Type</div>
                  <div className="info-value">{getProf.employment_type}</div>
                </div>
                
                <div className="info-item">
                  <div className="info-label">Monthly Income</div>
                  <div className="info-value">{numFor(getProf.income)}</div>
                </div>
                
                <div className="info-item">
                  <div className="info-label">Company</div>
                  {(user.user_id === getProf.user_id || allDataRevealed) ? (
                    <div className="info-value">{getProf.company_name || "Not specified"}</div>
                  ) : (user.pay_membership === "Gold" || user.pay_membership === "Silver" || user.pay_membership === "Diamond") ? (
                    // For paid users who haven't revealed this data yet
                    <div 
                      className="blurred-info clickable" 
                      onClick={handleRequestSensitiveData}
                    >
                      <img
                        src={blur}
                        alt="blurred"
                        style={{ width: "100%", height: "25px", opacity: 0.5 }}
                      />
                      <div className="view-text">Click to view</div>
                    </div>
                  ) : (
                    // For non-paid users
                    <div className="blurred-info">
                      <img
                        src={blur}
                        alt="upgrade"
                        style={{ width: "100%", height: "25px", opacity: 0.5 }}
                      />
                      <div onClick={() => {
                    
                    navigate('/payment/paymentoption');
                  }} className="upgrade-text">Upgrade to view</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Family Information */}
          <div className="profile-section">
            <h2 className="section-title">Family Information</h2>
            <div className="info-grid">
              <div className="info-item">
                <div className="info-label">Father's Name</div>
                <div className="info-value">{getProf.father_name}</div>
              </div>
              
              <div className="info-item">
                <div className="info-label">Mother's Name</div>
                <div className="info-value">{getProf.mother_name}</div>
              </div>
              
              <div className="info-item">
                <div className="info-label">Family Status</div>
                <div className="info-value">{getProf.family_status}</div>
              </div>
              
              <div className="info-item">
                <div className="info-label">Family Type</div>
                <div className="info-value">{getProf.family_type}</div>
              </div>
              
              <div className="info-item">
                <div className="info-label">Home Details</div>
                <div className="info-value">{getProf.home_details}</div>
              </div>
              
              <div className="info-item">
                <div className="info-label">Agricultural Land</div>
                <div className="info-value">{getProf.agricultural_land}</div>
              </div>
            </div>
          </div>

          {/* Religious Information */}
          <div className="profile-section">
            <h2 className="section-title">Religious Information</h2>
            <div className="info-grid">
              <div className="info-item">
                <div className="info-label">Religion</div>
                <div className="info-value">{getProf.religion}</div>
              </div>
              
              <div className="info-item">
                <div className="info-label">Caste</div>
                <div className="info-value">{getProf.caste}</div>
              </div>
              
              <div className="info-item">
                <div className="info-label">Sub Caste</div>
                <div className="info-value">{getProf.sub_caste}</div>
              </div>
              
              <div className="info-item">
                <div className="info-label">Kulam</div>
                <div className="info-value">{getProf.kulam}</div>
              </div>
              
              <div className="info-item">
                <div className="info-label">Star</div>
                <div className="info-value">{getProf.star}</div>
              </div>
              
              <div className="info-item">
                <div className="info-label">Raasi</div>
                <div className="info-value">{getProf.raasi}</div>
              </div>
              
              <div className="info-item">
                <div className="info-label">Chevvai Dosham</div>
                <div className="info-value">{getProf.chevvai_dosham === "chevvai" ? "Yes" : "No"}</div>
              </div>
              
              <div className="info-item">
                <div className="info-label">Naga Dosham</div>
                <div className="info-value">{getProf.naga_dosham === "naga" ? "Yes" : "No"}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Upgrade Modal */}
      {showUpgradeModal && (
        <div className="sensitive-data-modal">
          <div className="sensitive-data-modal-content">
            <h3>Upgrade Required</h3>
            <p>You need to upgrade your membership to view sensitive data.</p>
            <p>Premium memberships allow you to view contact details and other sensitive information.</p>
            <div className="sensitive-data-modal-buttons">
              <button onClick={() => setShowUpgradeModal(false)}>Close</button>
              <button 
                onClick={() => {
                  setShowUpgradeModal(false);
                  navigate('/payment/paymentoption');
                }}
                className="upgrade-button"
              >
                Upgrade Now
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Horoscope Section - Only show when data is loaded */}
      {!isLoadingUserData && Object.keys(getProf).length > 0 && (
        <>
          {(user.user_id === getProf.user_id || allDataRevealed) ? (
            <div style={{marginTop: "30px"}}>
              <h2 className="horoscope-title">Horoscope Details</h2>
              <div className="horoscope-charts-container">
                {/* Rasi Chart */}
                <div className="">
                  <div className="chart-grid">
                  <div className="chart-cell">
                      <span className="planet">{getProf.box1 || ""}</span>
                    </div>
                    <div className="chart-cell">
                      <span className="planet">{getProf.box2 || ""}</span>
                    </div>
                    <div className="chart-cell">
                      <span className="planet">{getProf.box3 || ""}</span>
                    </div>
                    <div className="chart-cell">
                      <span className="planet">{getProf.box4 || ""}</span>
                    </div>

                    <div className="chart-cell">
                      <span className="planet">{getProf.box5 || ""}</span>
                    </div>
                    <div className="chart-cell center-cell" rowSpan={2} colSpan={2}>
                      <div className="chart-title">ராசி</div>
                    </div>
                    <div className="chart-cell">
                      <span className="planet">{getProf.box6 || ""}</span>
                    </div>

                    <div className="chart-cell">
                      <span className="planet">{getProf.box7 || ""}</span>
                    </div>
                    <div className="chart-cell">
                      <span className="planet">{getProf.box8 || ""}</span>
                    </div>

                    <div className="chart-cell">
                      <span className="planet">{getProf.box9 || ""}</span>
                    </div>
                    <div className="chart-cell">
                      <span className="planet">{getProf.box10 || ""}</span>
                    </div>
                    <div className="chart-cell">
                      <span className="planet">{getProf.box11 || ""}</span>
                    </div>
                    <div className="chart-cell">
                      <span className="planet">{getProf.box12 || ""}</span>
                    </div>
                  </div>
                </div>

                {/* Navamsa Chart */}
                <div className="">
                  <div className="chart-grid">
                  <div className="chart-cell">
                      <span className="planet">{getProf.box13 || ""}</span>
                    </div>
                    <div className="chart-cell">
                      <span className="planet">{getProf.box14 || ""}</span>
                    </div>
                    <div className="chart-cell">
                      <span className="planet">{getProf.box15 || ""}</span>
                    </div>
                    <div className="chart-cell">
                      <span className="planet">{getProf.box16 || ""}</span>
                    </div>

                    <div className="chart-cell">
                      <span className="planet">{getProf.box17 || ""}</span>
                    </div>
                    <div className="chart-cell center-cell" rowSpan={2} colSpan={2}>
                      <div className="chart-title">நவம்சம்</div>
                    </div>
                    <div className="chart-cell">
                      <span className="planet">{getProf.box18 || ""}</span>
                    </div>

                    <div className="chart-cell">
                      <span className="planet">{getProf.box19 || ""}</span>
                    </div>
                    <div className="chart-cell">
                      <span className="planet">{getProf.box20 || ""}</span>
                    </div>

                    <div className="chart-cell">
                      <span className="planet">{getProf.box21 || ""}</span>
                    </div>
                    <div className="chart-cell">
                      <span className="planet">{getProf.box22 || ""}</span>
                    </div>
                    <div className="chart-cell">
                      <span className="planet">{getProf.box23 || ""}</span>
                    </div>
                    <div className="chart-cell">
                      <span className="planet">{getProf.box24 || ""}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (user.pay_membership === "Gold" || user.pay_membership === "Silver" || user.pay_membership === "Diamond") ? (
            // For paid users who haven't revealed horoscope yet
            <div style={{marginTop: "30px"}}>
              <h2 className="horoscope-title">Horoscope Details</h2>
              <div 
                className="blurred-horoscope clickable" 
                onClick={handleRequestSensitiveData}
              >
                <img src={blur} alt="blurred" className="blurred-chart"/>
                <div className="view-horoscope-text">Click to view horoscope chart</div>
              </div>
            </div>
          ) : (
            // For non-paid users
            <div style={{marginTop: "30px"}}>
              <h2 className="horoscope-title">Horoscope Details</h2>
              <div className="blurred-horoscope">
                <img src={blur} alt="upgrade" className="blurred-chart"/>
                <div onClick={() => {
                      
                      navigate('/payment/paymentoption');
                    }} className="upgrade-horoscope-text">Upgrade membership to view horoscope chart</div>
              </div>
            </div>
          )}

          {/* Profile Views Stats for Premium Users */}
          {(user.pay_membership === "Gold" || user.pay_membership === "Silver" || user.pay_membership === "Diamond") && (
            <div className="view-stats">
              <div className="view-stats-content">
                <p>
                  <span className="views-remaining">{remainingViews}</span> sensitive data views remaining 
                  out of <span className="views-total">{membershipLimits[user.pay_membership]}</span>
                </p>
                {remainingViews < 5 && (
                  <Link to="/payment/paymentoption" className="upgrade-link-small">
                    Upgrade for more views
                  </Link>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {/* Remaining Search Results */}
      <RemainingSearchResults />
    </section>
  );
}

export default ViewProfile;